import {
	FamilyDto,
	PageDto,
	SearchFilter,
	PageOptionsDto,
	LanguageFilter,
	DistrictFilter,
	ChildDto,
	CreateFamilyDto,
	UpdateFamilyDto,
	OneRosterFamilyDto,
	OneRosterSettingsDto,
	CreateOneRosterSettingsDto,
} from '@families-link/shared';
import api from '.';

const familiesApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getFamiliesList: builder.query<
			PageDto<FamilyDto>,
			PageOptionsDto & SearchFilter & { district?: string | null } & LanguageFilter & DistrictFilter
		>({
			query: ({ page, take, orderField, order, search, district, languages, districts }) => ({
				url: `families`,
				params: { page, take, orderField, order, search, district, languages, districts },
			}),
			providesTags: ['Family'],
		}),
		getAllFamiliesByDistrictId: builder.query<FamilyDto[], { district?: string | null }>({
			query: ({ district }) => ({
				url: `families/by/district`,
				params: { district },
			}),
			providesTags: ['Family'],
		}),
		getAllFamiliesByList: builder.query<FamilyDto[], { families?: string[] }>({
			query: ({ families }) => ({
				url: `families/by/families-list`,
				params: { families },
			}),
			providesTags: ['Family'],
		}),
		getFamily: builder.query<FamilyDto, { id?: string | null }>({
			query: ({ id }) => ({
				url: `families/${id}`,
			}),
			providesTags: ['Family'],
		}),
		generateFamilyPhotoUrl: builder.mutation<{ url: string }, FormData>({
			query: (data) => ({
				url: `families/photo-url`,
				method: 'POST',
				body: data,
			}),
		}),
		createFamily: builder.mutation<FamilyDto, CreateFamilyDto>({
			query: (data) => ({
				url: `families`,
				method: 'POST',
				body: data,
			}),
			invalidatesTags: ['Family'],
		}),
		updateFamily: builder.mutation<FamilyDto, { id?: string; data: UpdateFamilyDto }>({
			query: ({ id, data }) => ({
				url: `families/${id}`,
				method: 'PUT',
				body: data,
			}),
			invalidatesTags: ['Family'],
		}),
		deleteFamily: builder.mutation<FamilyDto, { id: string }>({
			query: ({ id }) => ({
				url: `families/${id}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Family'],
		}),
		importFamilies: builder.mutation<
			{ importedCount: number; success: any[]; errors: any[]; warnings: any[] },
			{ formData: FormData; districtId?: string | null }
		>({
			query: ({ formData, districtId }) => ({
				url: `import/families`,
				method: 'POST',
				body: formData,
				params: { district: districtId },
			}),
			invalidatesTags: ['Family'],
		}),
		getTemplateUrl: builder.query<{ url: string }, void>({
			query: () => ({
				url: 'import/families/template-url',
				method: 'GET',
			}),
		}),
		getChildrenByFamilyIds: builder.query<ChildDto[], { families?: string }>({
			query: ({ families }) => ({
				url: `families/children/by/families-list`,
				params: { families },
			}),
			providesTags: ['Family'],
		}),
		getChildrenByDistrictId: builder.query<ChildDto[], { districtId?: string | null }>({
			query: ({ districtId }) => ({
				url: `families/children/by/district/${districtId}`,
			}),
			providesTags: ['Family'],
		}),
		checkExistOneRosterSettings: builder.query<boolean, string>({
			query: (districtId) => ({
				url: `oneroster/settings/${districtId}`
			}),
			providesTags: ['OneRoster']
		}),
		saveOneRosterSettings: builder.mutation<void, CreateOneRosterSettingsDto>({
			query: (data) => ({
				url: `oneroster/settings`,
				method: 'POST',
				body: data
			}),
			invalidatesTags: ['OneRoster']
		}),
		getStudentsOneRoaster: builder.query<OneRosterFamilyDto[], string>({
			query: (districtId) => ({
				url: `oneroster/students/${districtId}`,
			}),
			providesTags: ['OneRoster']
		}),
		createFamilyArray: builder.mutation<{ success: any[]; failed: any[] }, any>({
			query: (data) => ({
				url: `families/array`,
				method: 'POST',
				body: data,
			}),
			invalidatesTags: ['Family'],
		}),
	}),
});

export default familiesApi;

export const {
	useGetFamiliesListQuery,
	useGetAllFamiliesByDistrictIdQuery,
	useGetFamilyQuery,
	useGetAllFamiliesByListQuery,
	useCreateFamilyMutation,
	useUpdateFamilyMutation,
	useDeleteFamilyMutation,
	useImportFamiliesMutation,
	useGetTemplateUrlQuery,
	useGetChildrenByFamilyIdsQuery,
	useGenerateFamilyPhotoUrlMutation,
	useGetChildrenByDistrictIdQuery,
	useCheckExistOneRosterSettingsQuery,
	useSaveOneRosterSettingsMutation,
	useGetStudentsOneRoasterQuery,
	useCreateFamilyArrayMutation,
} = familiesApi;
